/**
 * Os datasets do `BF_SELLER_DATASETS` indexados em memória.
 *
 * Com um registro por arquivo, ler "um produto" significa abrir o arquivo do catálogo inteiro.
 * Fazer isso a cada página seria pior que o formato antigo — então cada store é aberta **uma vez
 * por sessão**, indexada por código, e todo mundo lê daqui: lista paginada, carrinho, checkout,
 * hidratação das tabelas legadas.
 *
 * O que fica em memória é o dado cru dos arquivos, indexado. O produto montado (com preço,
 * embalagem recortada e última compra) continua sendo construído sob demanda, só para a página
 * visível — isso não mudou.
 *
 * Quem invalida é o sync, depois de gravar (`invalidateBfSellerCache`). Uma view que falha não
 * fica no cache: a próxima chamada tenta de novo.
 */

import { readValues } from './bfSellerReader';
import type { BfSellerStore } from './bfSellerSchema';

const isPlainObject = (value: any): value is Record<string, any> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const views = new Map<string, Promise<unknown>>();

const view = <T>(nome: string, build: () => Promise<T>): Promise<T> => {
  const atual = views.get(nome) as Promise<T> | undefined;
  if (atual) return atual;

  const nova = build().catch((error) => {
    views.delete(nome);
    throw error;
  });
  views.set(nome, nova);
  return nova;
};

/** Chamar depois de um sync — os arquivos mudaram. */
export const invalidateBfSellerCache = (): void => {
  views.clear();
};

/**
 * Os itens de uma store, em lista plana. Cada registro é um arquivo, então isto é a concatenação
 * dos arquivos — o formato que as telas sempre consumiram.
 */
export const readFlatRows = async <T = any>(store: BfSellerStore): Promise<T[]> => {
  const values = await readValues(store);
  const out: T[] = [];
  for (const value of values) {
    if (Array.isArray(value)) out.push(...(value as T[]));
    else if (value != null) out.push(value as T);
  }
  return out;
};

/** O primeiro objeto gravado numa store de registro único (`campanha_produto`, `campanha_cliente`). */
export const readSingleObject = async (store: BfSellerStore): Promise<Record<string, any>> => {
  const values = await readValues(store);
  return (values.find(isPlainObject) as Record<string, any>) || {};
};

/** Une os mapas `{ [id]: valor }` de todos os arquivos da store num Map só. */
const mapaPorId = async <T>(store: BfSellerStore, merge?: (anterior: T, novo: T) => T): Promise<Map<number, T>> => {
  const values = await readValues<Record<string, T>>(store);
  const out = new Map<number, T>();

  for (const mapa of values) {
    if (!isPlainObject(mapa)) continue;
    for (const [id, valor] of Object.entries(mapa)) {
      const cod = Number(id);
      if (!Number.isFinite(cod)) continue;
      const anterior = out.get(cod);
      out.set(cod, anterior !== undefined && merge ? merge(anterior, valor) : valor);
    }
  }

  return out;
};

/** Une `{ [loja]: { [segmento]: embalagens } }` de dois arquivos do mesmo produto. */
const mergePreco = (anterior: any, novo: any): any => {
  if (!isPlainObject(anterior)) return novo;
  const out: Record<string, any> = { ...anterior };
  for (const [loja, porSegmento] of Object.entries(novo || {})) {
    out[loja] = isPlainObject(out[loja]) && isPlainObject(porSegmento) ? { ...out[loja], ...porSegmento } : porSegmento;
  }
  return out;
};

// -------------------------------------------------------------------------------------------
// views
// -------------------------------------------------------------------------------------------

/**
 * Linhas cruas do catálogo por código. É lista porque o mesmo produto vem em mais de um arquivo
 * quando o RCA atende mais de uma loja — quem junta é o `buildProduct`/`mergeProducts`.
 */
export const catalogoPorCodigo = (): Promise<Map<number, any[]>> =>
  view('catalogo', async () => {
    const values = await readValues('catalogo');
    const out = new Map<number, any[]>();

    for (const arquivo of values) {
      for (const item of Array.isArray(arquivo) ? arquivo : []) {
        const cod = Number(item?.COD_PRODUTO);
        if (!Number.isFinite(cod)) continue;
        const bucket = out.get(cod);
        if (bucket) bucket.push(item);
        else out.set(cod, [item]);
      }
    }

    return out;
  });

/** Preço por produto: `{ [loja]: { [segmento]: { [qtd]: item } } }`, já unido entre arquivos. */
export const precoPorProduto = (): Promise<Map<number, any>> => view('preco', () => mapaPorId<any>('preco', mergePreco));

/** Última compra por cliente: `{ [produto]: linha }`. */
export const ultimaCompraPorCliente = (): Promise<Map<number, Record<string, any>>> =>
  view('ultima_compra', () => mapaPorId<Record<string, any>>('ultima_compra', (anterior, novo) => ({ ...anterior, ...novo })));

/** Produtos que cada cliente não pode comprar. */
export const restricoesPorCliente = (): Promise<Map<number, number[]>> =>
  view('restricaoProdutoCliente', () =>
    mapaPorId<number[]>('restricaoProdutoCliente', (anterior, novo) => [...anterior, ...novo])
  );

/** Preço promocional por cliente: árvore `[qtd][produto][loja][segmento]`. */
export const precoPromocaoPorCliente = (): Promise<Map<number, any>> =>
  view('precoPromocaoCliente', () => mapaPorId<any>('precoPromocaoCliente'));

/** Ciclo de venda por cliente, já ordenado por semana na gravação. */
export const cicloPorCliente = (): Promise<Map<number, any[]>> =>
  view('ciclo_venda_cliente_cliente', () =>
    mapaPorId<any[]>('ciclo_venda_cliente_cliente', (anterior, novo) => [...anterior, ...novo])
  );

/**
 * Linhas de cliente por código. É lista pelo mesmo motivo do catálogo: um cliente atendido por
 * duas lojas tem uma linha em cada arquivo.
 */
export const clientesPorCodigo = (): Promise<Map<number, any[]>> =>
  view('cliente', async () => {
    const values = await readValues('cliente');
    const out = new Map<number, any[]>();

    for (const arquivo of values) {
      for (const cliente of Array.isArray(arquivo) ? arquivo : []) {
        const cod = Number(cliente?.COD_CLIENTE);
        if (!Number.isFinite(cod)) continue;
        const bucket = out.get(cod);
        if (bucket) bucket.push(cliente);
        else out.set(cod, [cliente]);
      }
    }

    return out;
  });
